document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const normalizeEmail = (value) => {
  const email = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : 'hello@iguionss.com';
};
const normalizeWhatsAppNumber = (value) => String(value || '').replace(/\D/g, '') || '8618638275508';
const escapeMarkup = (value) => String(value).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

const storefrontContact = {
  email: normalizeEmail(document.body?.dataset.contactEmail),
  whatsappNumber: normalizeWhatsAppNumber(document.body?.dataset.whatsappNumber),
};
const storefrontWhatsAppUrl = `https://wa.me/${storefrontContact.whatsappNumber}`;

const updateGalleryCounter = (gallery, activeIndex, total) => {
  const current = gallery?.querySelector('[data-gallery-current]');
  const count = gallery?.querySelector('[data-gallery-total]');
  if (current) current.textContent = String(activeIndex + 1);
  if (count) count.textContent = String(total);
};

const updateGalleryNavigation = (gallery, activeIndex, total) => {
  const previous = gallery?.querySelector('[data-gallery-prev]');
  const next = gallery?.querySelector('[data-gallery-next]');
  if (previous) previous.disabled = activeIndex <= 0;
  if (next) next.disabled = activeIndex >= total - 1;
};

const syncGalleryVideoState = (item) => {
  const video = item?.querySelector('video');
  item?.classList.toggle('is-video-playing', Boolean(video && !video.paused && !video.ended));
};

const activateGalleryMedia = (gallery, target) => {
  const nextItem = gallery?.querySelector(`[data-gallery-main-item="${target}"]`);
  if (!gallery || !nextItem) return false;

  const main = gallery.querySelector('[data-gallery-main]');
  const items = Array.from(gallery.querySelectorAll('[data-gallery-main-item]'));
  const activeIndex = items.indexOf(nextItem);
  const swipeReady = gallery.classList.contains('is-swipe-ready');
  items.forEach((item) => {
    const isActive = item === nextItem;
    const video = item.querySelector('video');
    if (!isActive && video && !video.paused) video.pause();
    item.hidden = swipeReady ? false : !isActive;
    item.classList.toggle('is-active', isActive);
    syncGalleryVideoState(item);
  });
  updateGalleryCounter(gallery, activeIndex, items.length);
  updateGalleryNavigation(gallery, activeIndex, items.length);
  if (swipeReady && main) {
    gallery.dataset.activeIndex = String(activeIndex);
    main.style.transform = `translate3d(${-activeIndex * 100}%, 0, 0)`;
  }
  let activeThumbnail = null;
  gallery.querySelectorAll('[data-gallery-thumbnail]').forEach((item) => {
    const isActive = item.dataset.galleryThumbnail === target;
    item.classList.toggle('is-active', isActive);
    item.setAttribute('aria-selected', String(isActive));
    if (isActive) activeThumbnail = item;
  });
  const thumbnailStrip = activeThumbnail?.parentElement;
  if (thumbnailStrip) {
    const centeredLeft = activeThumbnail.offsetLeft - ((thumbnailStrip.clientWidth - activeThumbnail.offsetWidth) / 2);
    const centeredTop = activeThumbnail.offsetTop - ((thumbnailStrip.clientHeight - activeThumbnail.offsetHeight) / 2);
    thumbnailStrip.scrollTo({ left: Math.max(0, centeredLeft), top: Math.max(0, centeredTop), behavior: 'smooth' });
  }
  return true;
};

const getVariants = (variantPicker) => {
  try {
    return JSON.parse(variantPicker?.dataset.variants || '[]');
  } catch (error) {
    return [];
  }
};

const updateVariantButtons = (variantPicker) => {
  if (!variantPicker) return;
  const variants = getVariants(variantPicker);
  const selectedOptions = Array.from(variantPicker.querySelectorAll('select')).map((select) => select.value);

  variantPicker.querySelectorAll('[data-variant-option-group]').forEach((group) => {
    const optionIndex = Number.parseInt(group.dataset.optionIndex, 10);
    const selectedValue = selectedOptions[optionIndex] || '';
    const selectedLabel = group.querySelector('[data-variant-option-value]');
    if (selectedLabel) selectedLabel.textContent = selectedValue;

    group.querySelectorAll('[data-variant-option]').forEach((button) => {
      const optionValue = button.dataset.optionValue || '';
      const requestedOptions = [...selectedOptions];
      requestedOptions[optionIndex] = optionValue;
      const hasAvailableCombination = variants.some((variant) => variant.available && variant.options.every((option, index) => option === requestedOptions[index]));
      const isSelected = optionValue === selectedValue;
      button.disabled = !hasAvailableCombination;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
  });
};

const applyVariantState = (variantPicker, variant, { syncOptions = false, syncGallery = true } = {}) => {
  const form = variantPicker?.closest('form');
  const idInput = form?.querySelector('[name="id"]');
  if (!variantPicker || !variant || !idInput) return false;

  if (syncOptions) {
    variantPicker.querySelectorAll('select').forEach((select, index) => {
      if (variant.options[index] !== undefined) select.value = variant.options[index];
    });
  }
  updateVariantButtons(variantPicker);

  idInput.value = variant.id;
  const productPage = variantPicker.closest('.product-page');
  const hasCompareAtPrice = Number(variant.compareAtPriceAmount) > Number(variant.priceAmount);
  productPage?.querySelectorAll('[data-product-current-price]').forEach((price) => { price.textContent = variant.price; });
  productPage?.querySelectorAll('[data-product-compare-price]').forEach((price) => {
    price.textContent = hasCompareAtPrice ? variant.compareAtPrice : '';
    price.hidden = !hasCompareAtPrice;
  });
  productPage?.querySelectorAll('[data-product-submit], [data-sticky-product-submit]').forEach((button) => {
    button.disabled = !variant.available;
    button.textContent = variant.available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
  });
  if (syncGallery && variant.featuredMediaId) {
    const gallery = variantPicker.closest('.product-page')?.querySelector('[data-product-gallery]');
    activateGalleryMedia(gallery, `media-${variant.featuredMediaId}`);
  }

  const url = new URL(window.location.href);
  url.searchParams.set('variant', variant.id);
  window.history.replaceState({}, '', url);
  return true;
};

const syncVariantToGalleryMedia = (gallery, target) => {
  const variantPicker = gallery?.closest('.product-page')?.querySelector('[data-variant-picker]');
  if (!variantPicker) return false;

  const mediaId = Number(target.replace('media-', ''));
  const variants = getVariants(variantPicker);
  const candidates = variants.filter((variant) => Number(variant.featuredMediaId) === mediaId);
  if (!candidates.length) return false;

  const selectedOptions = Array.from(variantPicker.querySelectorAll('select')).map((select) => select.value);
  const variant = candidates.sort((first, second) => {
    const firstMatches = first.options.filter((option, index) => option === selectedOptions[index]).length;
    const secondMatches = second.options.filter((option, index) => option === selectedOptions[index]).length;
    return secondMatches - firstMatches;
  })[0];
  return applyVariantState(variantPicker, variant, { syncOptions: true, syncGallery: false });
};

document.addEventListener('change', (event) => {
  const variantPicker = event.target.closest('[data-variant-picker]');
  if (!variantPicker) return;

  const variants = getVariants(variantPicker);
  const selected = Array.from(variantPicker.querySelectorAll('select')).map((select) => select.value);
  const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
  applyVariantState(variantPicker, variant);
});

document.querySelectorAll('[data-variant-picker]').forEach((variantPicker) => updateVariantButtons(variantPicker));

document.querySelectorAll('.product-page').forEach((productPage) => {
  const stickyCart = productPage.querySelector('[data-mobile-sticky-cart]');
  const mainActions = productPage.querySelector('.product-form__actions');
  const mainSubmit = productPage.querySelector('[data-product-submit]');
  const stickySubmit = stickyCart?.querySelector('[data-sticky-product-submit]');
  if (!stickyCart || !mainActions || !mainSubmit || !stickySubmit) return;

  let updateQueued = false;
  const updateStickyCart = () => {
    updateQueued = false;
    const isMobile = window.matchMedia('(max-width: 800px)').matches;
    const hasPassedMainActions = mainActions.getBoundingClientRect().bottom < 0;
    const isVisible = isMobile && hasPassedMainActions;
    stickyCart.classList.toggle('is-visible', isVisible);
    stickyCart.setAttribute('aria-hidden', String(!isVisible));
    document.documentElement.classList.toggle('has-mobile-sticky-cart', isVisible);
  };
  const queueStickyCartUpdate = () => {
    if (updateQueued) return;
    updateQueued = true;
    window.requestAnimationFrame(updateStickyCart);
  };

  stickySubmit.addEventListener('click', () => {
    if (!mainSubmit.disabled) mainSubmit.click();
  });
  window.addEventListener('scroll', queueStickyCartUpdate, { passive: true });
  window.addEventListener('resize', queueStickyCartUpdate);
  updateStickyCart();
});

document.addEventListener('click', (event) => {
  const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
  if (quantityButton) {
    const quantitySelector = quantityButton.closest('[data-quantity-selector]');
    const input = quantitySelector?.querySelector('[data-quantity-input]');
    if (!input) return;

    const minimum = Number.parseInt(input.min, 10) || 1;
    const current = Number.parseInt(input.value, 10) || minimum;
    const change = quantityButton.hasAttribute('data-quantity-plus') ? 1 : -1;
    input.value = Math.max(minimum, current + change);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const variantOption = event.target.closest('[data-variant-option]');
  if (variantOption) {
    const variantPicker = variantOption.closest('[data-variant-picker]');
    const optionIndex = Number.parseInt(variantOption.dataset.optionIndex, 10);
    const select = variantPicker?.querySelectorAll('select')[optionIndex];
    if (!variantOption.disabled && select) {
      select.value = variantOption.dataset.optionValue || '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return;
  }

  const thumbnail = event.target.closest('[data-gallery-thumbnail]');
  if (!thumbnail) return;

  const gallery = thumbnail.closest('[data-product-gallery]');
  const target = thumbnail.dataset.galleryThumbnail;
  if (activateGalleryMedia(gallery, target)) syncVariantToGalleryMedia(gallery, target);
});

document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
  gallery.querySelectorAll('[data-gallery-main-item]').forEach((item) => {
    const video = item.querySelector('video');
    const playButton = item.querySelector('[data-gallery-video-play]');
    if (!video || !playButton) return;
    playButton.addEventListener('click', () => {
      const playback = video.play();
      if (playback?.catch) playback.catch(() => syncGalleryVideoState(item));
    });
    ['play', 'pause', 'ended'].forEach((eventName) => video.addEventListener(eventName, () => syncGalleryVideoState(item)));
    syncGalleryVideoState(item);
  });
  const viewport = gallery.querySelector('[data-gallery-viewport]');
  const main = gallery.querySelector('[data-gallery-main]');
  if (!viewport || !main) return;

  let dragState = null;
  let trackpadState = null;
  let trackpadTimer = null;
  const getItems = () => Array.from(gallery.querySelectorAll('[data-gallery-main-item]'));
  const moveGallery = (direction) => {
    const items = getItems();
    const activeIndex = Math.max(0, Number.parseInt(gallery.dataset.activeIndex, 10) || 0);
    const nextIndex = Math.min(items.length - 1, Math.max(0, activeIndex + direction));
    const target = items[nextIndex]?.dataset.galleryMainItem;
    if (target && activateGalleryMedia(gallery, target)) syncVariantToGalleryMedia(gallery, target);
  };
  const setGalleryLayout = () => {
    const items = getItems();
    const activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    updateGalleryCounter(gallery, activeIndex, items.length);
    updateGalleryNavigation(gallery, activeIndex, items.length);
    if (items.length > 1) {
      gallery.classList.add('is-swipe-ready');
      items.forEach((item) => { item.hidden = false; });
      gallery.dataset.activeIndex = String(activeIndex);
      main.style.transform = `translate3d(${-activeIndex * 100}%, 0, 0)`;
      return;
    }
    gallery.classList.remove('is-swipe-ready', 'is-dragging');
    items.forEach((item, index) => { item.hidden = index !== activeIndex; });
    main.style.transform = '';
  };

  const finishDrag = (event, cancelled = false) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    const state = dragState;
    dragState = null;
    gallery.classList.remove('is-dragging');
    if (viewport.hasPointerCapture?.(state.pointerId)) viewport.releasePointerCapture(state.pointerId);

    const items = getItems();
    const distanceX = state.lastX - state.startX;
    const distanceY = state.lastY - state.startY;
    const elapsed = Math.max(1, state.lastTime - state.startTime);
    const velocityX = distanceX / elapsed;
    const minimumDistance = Math.max(38, state.width * 0.1);
    const isSwipe = !cancelled
      && state.axis === 'horizontal'
      && Math.abs(distanceX) > Math.abs(distanceY) * 1.1
      && (Math.abs(distanceX) >= minimumDistance || Math.abs(velocityX) >= 0.45);
    const direction = isSwipe ? (distanceX < 0 ? 1 : -1) : 0;
    const nextIndex = Math.min(items.length - 1, Math.max(0, state.startIndex + direction));
    const target = items[nextIndex]?.dataset.galleryMainItem;
    if (target && activateGalleryMedia(gallery, target)) syncVariantToGalleryMedia(gallery, target);
  };

  const finishTrackpadGesture = (cancelled = false) => {
    if (!trackpadState) return;
    const state = trackpadState;
    trackpadState = null;
    if (trackpadTimer) window.clearTimeout(trackpadTimer);
    trackpadTimer = null;
    gallery.classList.remove('is-dragging');

    const items = getItems();
    const minimumDistance = Math.min(72, Math.max(32, state.width * 0.06));
    const isSwipe = !cancelled && Math.abs(state.distanceX) >= minimumDistance;
    const direction = isSwipe ? (state.distanceX > 0 ? 1 : -1) : 0;
    const nextIndex = Math.min(items.length - 1, Math.max(0, state.startIndex + direction));
    const target = items[nextIndex]?.dataset.galleryMainItem;
    if (target && activateGalleryMedia(gallery, target)) syncVariantToGalleryMedia(gallery, target);
  };

  gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => moveGallery(-1));
  gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => moveGallery(1));
  viewport.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveGallery(-1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveGallery(1);
    }
  });

  viewport.addEventListener('pointerdown', (event) => {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
      dragState = null;
      return;
    }
    if (event.target.closest('button, a, input, select, textarea, video, iframe')) return;
    finishTrackpadGesture();
    const items = getItems();
    const startIndex = Math.max(0, Number.parseInt(gallery.dataset.activeIndex, 10) || 0);
    dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      startTime: event.timeStamp,
      lastTime: event.timeStamp,
      startIndex,
      width: Math.max(1, viewport.clientWidth),
      itemCount: items.length,
      axis: null
    };
    viewport.setPointerCapture?.(event.pointerId);
  });

  viewport.addEventListener('pointermove', (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    dragState.lastX = event.clientX;
    dragState.lastY = event.clientY;
    dragState.lastTime = event.timeStamp;
    const distanceX = dragState.lastX - dragState.startX;
    const distanceY = dragState.lastY - dragState.startY;
    const absoluteX = Math.abs(distanceX);
    const absoluteY = Math.abs(distanceY);

    if (!dragState.axis && Math.max(absoluteX, absoluteY) >= 7) {
      if (absoluteX > absoluteY * 1.1) dragState.axis = 'horizontal';
      if (absoluteY > absoluteX * 1.1) dragState.axis = 'vertical';
    }
    if (dragState.axis === 'horizontal') {
      event.preventDefault();
      gallery.classList.add('is-dragging');
      const isPastStart = dragState.startIndex === 0 && distanceX > 0;
      const isPastEnd = dragState.startIndex === dragState.itemCount - 1 && distanceX < 0;
      const resistedDistance = (isPastStart || isPastEnd) ? distanceX * 0.28 : distanceX;
      const offset = (-dragState.startIndex * dragState.width) + resistedDistance;
      main.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
  });

  viewport.addEventListener('pointerup', (event) => finishDrag(event));
  viewport.addEventListener('pointercancel', (event) => finishDrag(event, true));
  viewport.addEventListener('lostpointercapture', (event) => finishDrag(event, true));
  viewport.addEventListener('dragstart', (event) => event.preventDefault());
  viewport.addEventListener('wheel', (event) => {
    if (event.ctrlKey || dragState) return;
    const absoluteX = Math.abs(event.deltaX);
    const absoluteY = Math.abs(event.deltaY);
    if (trackpadState && absoluteY > absoluteX * 1.25) {
      finishTrackpadGesture();
      return;
    }
    if (absoluteX < 2 || absoluteX <= absoluteY * 1.1) return;

    event.preventDefault();
    const items = getItems();
    if (items.length < 2) return;
    if (!trackpadState) {
      const startIndex = Math.max(0, Number.parseInt(gallery.dataset.activeIndex, 10) || 0);
      trackpadState = {
        startIndex,
        width: Math.max(1, viewport.clientWidth),
        itemCount: items.length,
        distanceX: 0
      };
    }

    const deltaScale = event.deltaMode === 1 ? 16 : (event.deltaMode === 2 ? trackpadState.width : 1);
    trackpadState.distanceX += event.deltaX * deltaScale;
    const isPastStart = trackpadState.startIndex === 0 && trackpadState.distanceX < 0;
    const isPastEnd = trackpadState.startIndex === trackpadState.itemCount - 1 && trackpadState.distanceX > 0;
    const resistedDistance = (isPastStart || isPastEnd) ? trackpadState.distanceX * 0.28 : trackpadState.distanceX;
    const offset = (-trackpadState.startIndex * trackpadState.width) - resistedDistance;
    gallery.classList.add('is-dragging');
    main.style.transform = `translate3d(${offset}px, 0, 0)`;

    if (trackpadTimer) window.clearTimeout(trackpadTimer);
    trackpadTimer = window.setTimeout(() => finishTrackpadGesture(), 140);
  }, { passive: false });
  window.addEventListener('resize', () => {
    finishTrackpadGesture(true);
    setGalleryLayout();
  });
  setGalleryLayout();
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-quantity-selector] [data-quantity-input]');
  if (!input) return;

  const minimum = Number.parseInt(input.min, 10) || 1;
  const quantity = Number.parseInt(input.value, 10);
  input.value = Number.isFinite(quantity) ? Math.max(minimum, quantity) : minimum;
});

document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
  button.addEventListener('click', () => {
    const header = button.closest('[data-site-header]');
    const panel = header?.querySelector('[data-search-panel]');
    const mobileSearchBar = header?.querySelector('[data-mobile-search-bar]');
    const mobileSearchButton = header?.querySelector('[data-mobile-search-toggle]');
    const isOpen = !header?.classList.contains('is-open');
    header?.classList.toggle('is-open', isOpen);
    if (panel) {
      panel.hidden = true;
      header.classList.remove('is-search-open');
    }
    if (mobileSearchBar) {
      mobileSearchBar.hidden = true;
      header.classList.remove('is-mobile-search-open');
      mobileSearchButton?.setAttribute('aria-expanded', 'false');
    }
    button.setAttribute('aria-expanded', String(Boolean(isOpen)));
  });
});

document.querySelectorAll('[data-site-header]').forEach((header) => {
  const stickyWrapper = header.closest('.shopify-section') || header;
  const panel = header.querySelector('[data-search-panel]');
  const search = header.querySelector('[data-header-search]');
  const searchInput = search?.querySelector('[type="search"]');
  const menuButton = header.querySelector('[data-menu-toggle]');
  const mobileSearchButton = header.querySelector('[data-mobile-search-toggle]');
  const mobileSearchBar = header.querySelector('[data-mobile-search-bar]');
  const mobileSearchInput = header.querySelector('[data-mobile-search-input]');
  let stickyFrame = null;
  stickyWrapper?.classList.add('sticky-header-wrapper');
  const updateStickyState = () => {
    stickyFrame = null;
    const isScrolled = header.classList.contains('is-scrolled');
    const shouldCompact = isScrolled ? window.scrollY > 20 : window.scrollY > 80;
    header.classList.toggle('is-scrolled', shouldCompact);
  };
  const requestStickyUpdate = () => {
    if (stickyFrame !== null) return;
    stickyFrame = window.requestAnimationFrame(updateStickyState);
  };
  updateStickyState();
  window.addEventListener('scroll', requestStickyUpdate, { passive: true });
  const closeMobileSearch = () => {
    if (!mobileSearchBar) return;
    mobileSearchBar.hidden = true;
    header.classList.remove('is-mobile-search-open');
    mobileSearchButton?.setAttribute('aria-expanded', 'false');
  };
  const openMobileSearch = () => {
    if (!mobileSearchBar) return;
    header.classList.remove('is-open');
    menuButton?.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    header.classList.remove('is-search-open');
    mobileSearchBar.hidden = false;
    header.classList.add('is-mobile-search-open');
    mobileSearchButton?.setAttribute('aria-expanded', 'true');
    window.requestAnimationFrame(() => mobileSearchInput?.focus());
  };
  const closePanel = () => {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    header.classList.remove('is-search-open');
    menuButton?.setAttribute('aria-expanded', 'false');
  };
  const openPanel = () => {
    if (!panel) return;
    header.classList.remove('is-open');
    panel.hidden = false;
    header.classList.add('is-search-open');
    menuButton?.setAttribute('aria-expanded', 'false');
  };
  search?.addEventListener('mouseenter', openPanel);
  searchInput?.addEventListener('focus', openPanel);
  header.addEventListener('mouseleave', closePanel);
  window.addEventListener('scroll', closePanel, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePanel();
  });
  mobileSearchButton?.addEventListener('click', () => {
    if (header.classList.contains('is-mobile-search-open')) closeMobileSearch();
    else openMobileSearch();
  });
  header.querySelector('[data-mobile-search-close]')?.addEventListener('click', closeMobileSearch);
  header.querySelector('[data-search-panel-close]')?.addEventListener('click', closePanel);
  document.addEventListener('click', (event) => { if (!header.contains(event.target)) closePanel(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { closePanel(); closeMobileSearch(); } });
});

document.querySelectorAll('[data-category-carousel]').forEach((carousel) => {
  const track = carousel.querySelector('[data-carousel-track]');
  const scrollByCard = (direction) => track?.scrollBy({ left: direction * (carousel.hasAttribute('data-carousel-page') ? track.clientWidth : Math.max(260, track.clientWidth * .72)), behavior: 'smooth' });
  carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => scrollByCard(-1));
  carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => scrollByCard(1));
});

document.querySelectorAll('[data-mobile-carousel]').forEach((track) => {
  const items = Array.from(track.querySelectorAll('[data-mobile-carousel-item]'));
  const counter = track.parentElement?.querySelector('[data-mobile-carousel-current]');
  if (!items.length || !counter) return;

  let frame = null;
  const updateCounter = () => {
    frame = null;
    const trackLeft = track.getBoundingClientRect().left;
    const activeIndex = items.reduce((closestIndex, item, index) => {
      const closestDistance = Math.abs(items[closestIndex].getBoundingClientRect().left - trackLeft);
      const itemDistance = Math.abs(item.getBoundingClientRect().left - trackLeft);
      return itemDistance < closestDistance ? index : closestIndex;
    }, 0);
    counter.textContent = String(activeIndex + 1).padStart(2, '0');
  };
  track.addEventListener('scroll', () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(updateCounter);
  }, { passive: true });
  updateCounter();
});

const storefrontFooter = document.querySelector('.site-footer');
const whatsappFloat = document.querySelector('[data-whatsapp-float]');
if (storefrontFooter && whatsappFloat && 'IntersectionObserver' in window) {
  const footerObserver = new IntersectionObserver((entries) => {
    whatsappFloat.classList.toggle('is-footer-visible', entries.some((entry) => entry.isIntersecting));
  });
  footerObserver.observe(storefrontFooter);
}

// On small screens, temporarily clear the floating contact button while the
// Factory Proof section is visible so it cannot cover the final proof point.
const factoryProof = document.querySelector('.factory-proof');
const mobileViewport = window.matchMedia('(max-width: 800px)');
if (factoryProof && whatsappFloat && 'IntersectionObserver' in window) {
  const factoryProofObserver = new IntersectionObserver((entries) => {
    const isVisible = entries.some((entry) => entry.isIntersecting);
    whatsappFloat.classList.toggle('is-factory-proof-visible', mobileViewport.matches && isVisible);
  }, { threshold: 0.08 });
  factoryProofObserver.observe(factoryProof);
  const resetFactoryProofFloat = () => {
    if (!mobileViewport.matches) whatsappFloat.classList.remove('is-factory-proof-visible');
  };
  mobileViewport.addEventListener?.('change', resetFactoryProofFloat);
}

// V42.4 legacy request controls are retained as dormant rollback code.
// not a cart item or automatic checkout. The selected row count drives mixed-length fields.
(() => {
  const page = document.querySelector('[data-custom-order-request-v424]');
  if (!page) return;

  const getChecked = (selector, scope = page) => scope.querySelector(`${selector}:checked`);
  const formatInputs = [...page.querySelectorAll('[data-request-format]')];
  const modelInputs = [...page.querySelectorAll('[data-request-model]')];
  const boxInputs = [...page.querySelectorAll('[data-box-style]')];
  const packageInputs = [...page.querySelectorAll('[data-request-package]')];
  const modelPanels = [...page.querySelectorAll('[data-model-panel]')];
  const summary = {
    format: page.querySelector('[data-summary-format]'),
    model: page.querySelector('[data-summary-model]'),
    color: page.querySelector('[data-summary-color]'),
    length: page.querySelector('[data-summary-length]'),
    box: page.querySelector('[data-summary-box]'),
    package: page.querySelector('[data-summary-package]'),
  };
  const emailLink = page.querySelector('[data-request-email]');
  const whatsAppLink = page.querySelector('[data-request-whatsapp]');

  const parseLengths = (value) => String(value || '')
    .split(',')
    .map((length) => length.trim())
    .filter(Boolean);

  const activeModel = () => {
    const selected = getChecked('[data-request-model]');
    return selected ? page.querySelector(`[data-model-panel="${selected.dataset.modelKey}"]`) : null;
  };

  const activeLengths = () => parseLengths(activeModel()?.dataset.modelLengths || getChecked('[data-request-model]')?.dataset.lengths);
  const selectedFormat = () => getChecked('[data-request-format]');

  const buildLengthControls = () => {
    const panel = activeModel();
    if (!panel) return;
    const controls = panel.querySelector('[data-length-controls]');
    const lengths = activeLengths();
    const format = selectedFormat();
    const rowCount = Math.max(1, Number(format?.dataset.rowCount || 3));
    if (!controls || !lengths.length) return;
    const optionMarkup = lengths.map((length) => `<option value="${escapeMarkup(length)}">${escapeMarkup(length)} mm</option>`).join('');
    const presetValues = ['10', '12', '14'].map((length, index) => lengths.includes(length) ? length : lengths[Math.min(index, lengths.length - 1)]);
    const rowMarkup = Array.from({ length: rowCount }, (_, index) => `<label>Row ${index + 1}<select data-custom-row-length>${optionMarkup}</select></label>`).join('');
    controls.innerHTML = `
      <fieldset class="packaging-page__length-mode"><legend>Length configuration</legend>
        <label><input type="radio" name="length-mode" value="single" data-length-mode checked> Single length</label>
        <label><input type="radio" name="length-mode" value="preset" data-length-mode> Mixed preset (${presetValues.join(' / ')} mm)</label>
        <label><input type="radio" name="length-mode" value="custom" data-length-mode> Custom row-by-row mix</label>
      </fieldset>
      <div class="packaging-page__length-choice" data-length-single><label>Length<select data-single-length>${optionMarkup}</select></label></div>
      <div class="packaging-page__row-lengths" data-row-lengths hidden>${rowMarkup}</div>
    `;
    const rowSelects = [...controls.querySelectorAll('[data-custom-row-length]')];
    rowSelects.forEach((select, index) => { select.value = presetValues[index % presetValues.length] || lengths[0]; });
    controls.querySelectorAll('input[data-length-mode], select').forEach((input) => input.addEventListener('change', sync));
  };

  const getLengthSummary = () => {
    const panel = activeModel();
    const controls = panel?.querySelector('[data-length-controls]');
    const mode = getChecked('[data-length-mode]', controls)?.value || 'single';
    const lengths = activeLengths();
    if (mode === 'single') return `Single length · ${controls?.querySelector('[data-single-length]')?.value || lengths[0]} mm`;
    const rowValues = [...(controls?.querySelectorAll('[data-custom-row-length]') || [])].map((input) => `${input.value} mm`);
    if (mode === 'preset') return `Mixed-length preset · ${rowValues.join(' / ')}`;
    return `Custom ${rowValues.length}-row mix · ${rowValues.map((value, index) => `Row ${index + 1}: ${value}`).join('; ')}`;
  };

  const syncLengthMode = () => {
    const panel = activeModel();
    const controls = panel?.querySelector('[data-length-controls]');
    const mode = getChecked('[data-length-mode]', controls)?.value || 'single';
    const single = controls?.querySelector('[data-length-single]');
    const rows = controls?.querySelector('[data-row-lengths]');
    if (single) single.hidden = mode !== 'single';
    if (rows) rows.hidden = mode === 'single';
  };

  const syncSelectedCards = (inputs, cardSelector) => {
    const selected = inputs.find((input) => input.checked);
    inputs.forEach((input) => input.closest(cardSelector)?.classList.toggle('is-selected', input === selected));
  };

  const buildMessage = () => [
    'Hello, I would like to confirm a custom lash package request.',
    '',
    `Backing-card format: ${summary.format?.textContent || ''}`,
    `Lash model: ${summary.model?.textContent || ''}`,
    `Color: ${summary.color?.textContent || ''}`,
    `Length configuration: ${summary.length?.textContent || ''}`,
    `Box style: ${summary.box?.textContent || ''}`,
    `Package: ${summary.package?.textContent || ''}`,
    '',
    'Please contact me to review the order before payment. I will attach my artwork or logo in this message.',
  ].join('\n');

  const sync = () => {
    const format = selectedFormat();
    const model = getChecked('[data-request-model]');
    const panel = activeModel();
    const color = getChecked('[data-request-color]', panel);
    const box = getChecked('[data-box-style]');
    const packageChoice = getChecked('[data-request-package]');
    const formatLabel = format ? `${format.value} · ${format.dataset.cardSize}` : 'To be confirmed';
    const modelLabel = model ? `${model.value}${model.dataset.curl ? ` · ${model.dataset.curl}` : ''}` : 'To be confirmed';
    const packageLabel = packageChoice ? `${packageChoice.dataset.boxes} Boxes · ${packageChoice.dataset.total} total` : 'To be confirmed';

    syncSelectedCards(formatInputs, '.packaging-page__format-option');
    syncSelectedCards(modelInputs, '.packaging-page__model-option');
    syncSelectedCards(boxInputs, '.packaging-page__box-style');
    syncSelectedCards(packageInputs, '.packaging-page__package');
    modelPanels.forEach((modelPanel) => modelPanel.classList.toggle('is-active', modelPanel === panel));
    panel?.querySelectorAll('[data-request-color]').forEach((input) => input.closest('.packaging-page__color-option')?.classList.toggle('is-selected', input === color));
    syncLengthMode();

    if (summary.format) summary.format.textContent = formatLabel;
    if (summary.model) summary.model.textContent = modelLabel;
    if (summary.color) summary.color.textContent = color?.value || 'To be confirmed';
    if (summary.length) summary.length.textContent = getLengthSummary();
    if (summary.box) summary.box.textContent = box?.value || 'To be confirmed with our team';
    if (summary.package) summary.package.textContent = packageLabel;

    const message = buildMessage();
    if (emailLink) emailLink.href = `mailto:${storefrontContact.email}?subject=${encodeURIComponent('Custom Lash Package Request')}&body=${encodeURIComponent(message)}`;
    if (whatsAppLink) whatsAppLink.href = `${storefrontWhatsAppUrl}?text=${encodeURIComponent(message)}`;
  };

  formatInputs.forEach((input) => input.addEventListener('change', () => { buildLengthControls(); sync(); }));
  modelInputs.forEach((input) => input.addEventListener('change', () => { buildLengthControls(); sync(); }));
  boxInputs.forEach((input) => input.addEventListener('change', sync));
  packageInputs.forEach((input) => input.addEventListener('change', sync));
  modelPanels.forEach((panel) => panel.querySelectorAll('[data-request-color]').forEach((input) => input.addEventListener('change', sync)));
  buildLengthControls();
  sync();
})();

// V42.5 OEM-first request builder. Model rules are supplied by Theme Editor blocks as
// curl-to-length data; selected row count determines the number of independent row fields.
(() => {
  const page = document.querySelector('[data-custom-order-request]');
  if (!page) return;

  const checked = (selector, scope = page) => scope?.querySelector(`${selector}:checked`);
  const formatInputs = [...page.querySelectorAll('[data-request-format]')];
  const modelInputs = [...page.querySelectorAll('[data-request-model]')];
  const panels = [...page.querySelectorAll('[data-model-panel]')];
  const boxInputs = [...page.querySelectorAll('[data-box-style]')];
  const quantityInput = page.querySelector('[data-request-quantity]');
  const nameInput = page.querySelector('[data-request-name]');
  const contactInput = page.querySelector('[data-request-contact]');
  const destinationInput = page.querySelector('[data-request-destination]');
  const notesInput = page.querySelector('[data-request-notes]');
  const emailLink = page.querySelector('[data-request-email]');
  const whatsAppLink = page.querySelector('[data-request-whatsapp]');
  const summary = {
    format: page.querySelector('[data-summary-format]'),
    model: page.querySelector('[data-summary-model]'),
    color: page.querySelector('[data-summary-color]'),
    curl: page.querySelector('[data-summary-curl]'),
    length: page.querySelector('[data-summary-length]'),
    box: page.querySelector('[data-summary-box]'),
    quantity: page.querySelector('[data-summary-quantity]'),
    contact: page.querySelector('[data-summary-contact]'),
  };

  const activeModelInput = () => checked('[data-request-model]');
  const activePanel = () => {
    const model = activeModelInput();
    return model ? page.querySelector(`[data-model-panel="${model.dataset.modelKey}"]`) : null;
  };
  const selectedFormat = () => checked('[data-request-format]');
  const cleanList = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  const expandLengths = (specification) => {
    const values = [];
    cleanList(specification).forEach((part) => {
      const range = part.match(/^(\d+)\s*-\s*(\d+)$/);
      if (range) {
        const start = Number(range[1]);
        const end = Number(range[2]);
        for (let value = Math.min(start, end); value <= Math.max(start, end); value += 1) values.push(String(value));
      } else if (/^\d+$/.test(part)) {
        values.push(String(Number(part)));
      }
    });
    return [...new Set(values)].sort((a, b) => Number(a) - Number(b));
  };

  const ruleMap = (value) => String(value || '').split(';').reduce((rules, entry) => {
    const separator = entry.indexOf('=');
    if (separator < 1) return rules;
    const curl = entry.slice(0, separator).trim().toUpperCase();
    const lengths = expandLengths(entry.slice(separator + 1));
    if (curl && lengths.length) rules[curl] = lengths;
    return rules;
  }, {});

  const renderLengths = () => {
    const model = activeModelInput();
    const panel = activePanel();
    const controls = panel?.querySelector('[data-length-controls]');
    const previousMode = checked('[data-length-mode]', controls)?.value || 'single';
    const previousSingleLength = controls?.querySelector('[data-single-length]')?.value;
    const previousRowLengths = [...(controls?.querySelectorAll('[data-custom-row-length]') || [])].map((row) => row.value);
    const curl = checked('[data-request-curl]', panel)?.value;
    const lengths = ruleMap(model?.dataset.lengthRules)[curl] || [];
    const rowCount = Math.max(3, Number(selectedFormat()?.dataset.rowCount || 3));
    if (!controls) return;
    if (!lengths.length) {
      controls.innerHTML = '<p class="packaging-page__configuration-warning">Length options need manual confirmation for this model and curl.</p>';
      return;
    }
    const options = lengths.map((length) => `<option value="${escapeMarkup(length)}">${escapeMarkup(length)} mm</option>`).join('');
    const rowFields = Array.from({ length: rowCount }, (_, index) => `<label>Row ${index + 1}<select data-custom-row-length>${options}</select></label>`).join('');
    const groupName = `length-mode-${escapeMarkup(model?.dataset.modelKey || 'default')}`;
    controls.innerHTML = `
      <fieldset class="packaging-page__length-mode"><legend>Length method</legend>
        <label><input type="radio" name="${groupName}" value="single" data-length-mode checked> Single length for every row</label>
        <label><input type="radio" name="${groupName}" value="custom" data-length-mode> Custom mixed lengths — choose the length for each row</label>
      </fieldset>
      <div class="packaging-page__length-choice" data-length-single><label>Length<select data-single-length>${options}</select></label></div>
      <div class="packaging-page__row-lengths" data-row-lengths hidden>${rowFields}</div>
    `;
    const restoredMode = controls.querySelector(`[data-length-mode][value="${previousMode}"]`);
    if (restoredMode) restoredMode.checked = true;
    const singleLength = controls.querySelector('[data-single-length]');
    if (singleLength && previousSingleLength && lengths.includes(previousSingleLength)) singleLength.value = previousSingleLength;
    controls.querySelectorAll('[data-custom-row-length]').forEach((row, index) => {
      const previousValue = previousRowLengths[index] || previousRowLengths[previousRowLengths.length - 1];
      if (previousValue && lengths.includes(previousValue)) row.value = previousValue;
    });
    controls.querySelectorAll('input, select').forEach((control) => control.addEventListener('change', sync));
  };

  const renderModelControls = () => {
    const model = activeModelInput();
    const panel = activePanel();
    if (!model || !panel) return;
    const curlControls = panel.querySelector('[data-curl-controls]');
    const rules = ruleMap(model.dataset.lengthRules);
    const curls = cleanList(model.dataset.curls).map((curl) => curl.toUpperCase()).filter((curl) => rules[curl]?.length);
    if (curlControls) {
      curlControls.innerHTML = `<fieldset class="packaging-page__curl-picker"><legend>Curl</legend>${curls.map((curl, index) => `<label><input type="radio" name="curl-${escapeMarkup(model.dataset.modelKey)}" value="${escapeMarkup(curl)}" data-request-curl${index === 0 ? ' checked' : ''}> ${escapeMarkup(curl)} Curl</label>`).join('')}</fieldset>`;
      curlControls.querySelectorAll('[data-request-curl]').forEach((input) => input.addEventListener('change', () => { renderLengths(); sync(); }));
    }
    renderLengths();
  };

  const syncCards = (inputs, selector) => {
    const selected = inputs.find((input) => input.checked);
    inputs.forEach((input) => input.closest(selector)?.classList.toggle('is-selected', input === selected));
  };

  const lengthSummary = () => {
    const panel = activePanel();
    const controls = panel?.querySelector('[data-length-controls]');
    const mode = checked('[data-length-mode]', controls)?.value || 'single';
    if (mode === 'single') return `Single length · ${controls?.querySelector('[data-single-length]')?.value || 'To be confirmed'} mm`;
    const rows = [...(controls?.querySelectorAll('[data-custom-row-length]') || [])];
    return `Custom ${rows.length}-row mix · ${rows.map((row, index) => `Row ${index + 1}: ${row.value} mm`).join('; ')}`;
  };

  const showLengthMode = () => {
    const controls = activePanel()?.querySelector('[data-length-controls]');
    const custom = checked('[data-length-mode]', controls)?.value === 'custom';
    const single = controls?.querySelector('[data-length-single]');
    const rows = controls?.querySelector('[data-row-lengths]');
    if (single) single.hidden = custom;
    if (rows) rows.hidden = !custom;
  };

  const valueOr = (input, fallback = 'Not provided') => input?.value.trim() || fallback;
  const buildMessage = () => [
    'Hello, I would like a manual review of this custom lash packaging request.',
    '',
    `Model: ${summary.model?.textContent || ''}`,
    `Color: ${summary.color?.textContent || ''}`,
    `Curl: ${summary.curl?.textContent || ''}`,
    `Lengths: ${summary.length?.textContent || ''}`,
    `Box / packaging: ${summary.box?.textContent || ''}`,
    `Format / row count: ${summary.format?.textContent || ''}`,
    `Estimated quantity: ${summary.quantity?.textContent || ''}`,
    `Name / Brand: ${valueOr(nameInput)}`,
    `Contact: ${valueOr(contactInput)}`,
    `Destination: ${valueOr(destinationInput)}`,
    `Notes: ${valueOr(notesInput)}`,
    '',
    'Please confirm the available specification, quote and order details before creating a Shopify Draft Order or requesting payment.',
  ].join('\n');

  function sync() {
    const format = selectedFormat();
    const model = activeModelInput();
    const panel = activePanel();
    const color = checked('[data-request-color]', panel);
    const curl = checked('[data-request-curl]', panel);
    const box = checked('[data-box-style]');
    syncCards(formatInputs, '.packaging-page__format-option');
    syncCards(modelInputs, '.packaging-page__model-option');
    syncCards(boxInputs, '.packaging-page__box-style');
    panels.forEach((item) => item.classList.toggle('is-active', item === panel));
    panel?.querySelectorAll('[data-request-color]').forEach((input) => input.closest('.packaging-page__color-option')?.classList.toggle('is-selected', input === color));
    showLengthMode();
    if (summary.format) summary.format.textContent = format ? `${format.value} · ${format.dataset.rowCount} rows${format.dataset.cardSize ? ` · ${format.dataset.cardSize}` : ''}` : 'To be confirmed';
    if (summary.model) summary.model.textContent = model?.value || 'To be confirmed';
    if (summary.color) summary.color.textContent = color?.value || 'To be confirmed';
    if (summary.curl) summary.curl.textContent = curl ? `${curl.value} Curl` : 'To be confirmed';
    if (summary.length) summary.length.textContent = lengthSummary();
    if (summary.box) summary.box.textContent = box?.value || 'To be confirmed with our team';
    if (summary.quantity) summary.quantity.textContent = valueOr(quantityInput);
    if (summary.contact) summary.contact.textContent = [valueOr(nameInput, ''), valueOr(contactInput, '')].filter(Boolean).join(' · ') || 'Not provided';
    const message = buildMessage();
    if (emailLink) emailLink.href = `mailto:${storefrontContact.email}?subject=${encodeURIComponent('Custom Lash Packaging Request')}&body=${encodeURIComponent(message)}`;
    if (whatsAppLink) whatsAppLink.href = `${storefrontWhatsAppUrl}?text=${encodeURIComponent(message)}`;
  }

  formatInputs.forEach((input) => input.addEventListener('change', () => { renderLengths(); sync(); }));
  modelInputs.forEach((input) => input.addEventListener('change', () => { panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.modelPanel === input.dataset.modelKey)); renderModelControls(); sync(); }));
  boxInputs.forEach((input) => input.addEventListener('change', sync));
  panels.forEach((panel) => panel.querySelectorAll('[data-request-color]').forEach((input) => input.addEventListener('change', sync)));
  [quantityInput, nameInput, contactInput, destinationInput, notesInput].forEach((input) => input?.addEventListener('input', sync));
  renderModelControls();
  sync();
})();

const cartDrawer = document.querySelector('[data-cart-drawer]');

if (cartDrawer) {
  const cartUrl = cartDrawer.dataset.cartUrl || '/cart';
  const currency = cartDrawer.dataset.currency || 'USD';
  const panel = cartDrawer.querySelector('.cart-drawer__panel');
  const itemsContainer = cartDrawer.querySelector('[data-cart-drawer-items]');
  const footer = cartDrawer.querySelector('[data-cart-drawer-footer]');
  const total = cartDrawer.querySelector('[data-cart-drawer-total]');
  const status = cartDrawer.querySelector('[data-cart-status]');
  const closeButton = panel.querySelector('[data-cart-close]');
  const cartToast = document.querySelector('[data-cart-toast]');
  const cartToastMessage = cartToast?.querySelector('[data-cart-toast-message]');
  let lastFocusedElement = null;
  let cartOpenSequence = 0;
  let backgroundInertState = [];
  const cartFocusable = () => Array.from(panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
    .filter((element) => !element.disabled && element.tabIndex >= 0 && !element.closest('[hidden], [inert]') && element.getClientRects().length);
  const focusCart = () => (cartFocusable()[0] || panel).focus();
  const lockCartBackground = () => {
    if (backgroundInertState.length) return;
    backgroundInertState = Array.from(document.body.children)
      .filter((element) => element !== cartDrawer && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName))
      .map((element) => ({ element, wasInert: element.inert }));
    backgroundInertState.forEach(({ element }) => { element.inert = true; });
  };
  const unlockCartBackground = () => {
    backgroundInertState.forEach(({ element, wasInert }) => { element.inert = wasInert; });
    backgroundInertState = [];
  };
  document.addEventListener('focusin', (event) => {
    if (cartDrawer.classList.contains('is-open') && !panel.contains(event.target)) focusCart();
  });
  let isUpdating = false;
  let cartToastTimer = null;
  let cartToastHiddenTimer = null;

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const money = (amount) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'code'
      }).format((Number(amount) || 0) / 100);
    } catch (error) {
      return `$${((Number(amount) || 0) / 100).toFixed(2)}`;
    }
  };

  const setStatus = (message) => {
    if (status) status.textContent = message;
  };

  const hideCartToast = () => {
    if (!cartToast) return;
    window.clearTimeout(cartToastTimer);
    window.clearTimeout(cartToastHiddenTimer);
    cartToast.classList.remove('is-visible');
    cartToastHiddenTimer = window.setTimeout(() => { cartToast.hidden = true; }, 240);
  };

  const showCartToast = (message) => {
    if (!cartToast) return;
    window.clearTimeout(cartToastTimer);
    window.clearTimeout(cartToastHiddenTimer);
    if (cartToastMessage) cartToastMessage.textContent = message;
    cartToast.hidden = false;
    window.requestAnimationFrame(() => cartToast.classList.add('is-visible'));
    cartToastTimer = window.setTimeout(hideCartToast, 4000);
  };

  cartToast?.querySelector('[data-cart-toast-close]')?.addEventListener('click', hideCartToast);

  const productFeedbackButtons = (form) => {
    const productPage = form.closest('.product-page');
    return Array.from(productPage?.querySelectorAll('[data-product-submit], [data-sticky-product-submit]') || []);
  };

  const showAddedButtonState = (buttons) => {
    buttons.forEach((button) => {
      window.clearTimeout(Number(button.dataset.cartFeedbackTimer) || 0);
      button.classList.add('is-added');
      button.textContent = '\u2713 Added';
      const timer = window.setTimeout(() => {
        button.classList.remove('is-added');
        button.textContent = button.dataset.availableLabel || 'Add to Cart';
        delete button.dataset.cartFeedbackTimer;
      }, 1800);
      button.dataset.cartFeedbackTimer = String(timer);
    });
  };

  const animateProductToCart = (form) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const sourceImage = form.closest('.product-page')?.querySelector('[data-gallery-main-item].is-active img');
    if (!sourceImage?.currentSrc && !sourceImage?.src) return;

    const imageRect = sourceImage.getBoundingClientRect();
    const submitRect = form.querySelector('[data-product-submit]')?.getBoundingClientRect();
    if (!imageRect.width || !imageRect.height) return;
    const imageIsVisible = imageRect.bottom > 0 && imageRect.top < window.innerHeight && imageRect.right > 0 && imageRect.left < window.innerWidth;
    const sourceRect = imageIsVisible || !submitRect ? imageRect : submitRect;
    const cartTarget = document.querySelector('[data-cart-toggle]');
    const targetRect = cartTarget?.getBoundingClientRect();
    const targetIsVisible = targetRect && targetRect.bottom > 0 && targetRect.top < window.innerHeight && targetRect.right > 0 && targetRect.left < window.innerWidth;
    const endX = targetIsVisible ? targetRect.left + (targetRect.width / 2) : window.innerWidth - 28;
    const endY = targetIsVisible ? targetRect.top + (targetRect.height / 2) : 28;
    const size = Math.min(88, Math.max(68, sourceRect.width * .18));
    const startX = sourceRect.left + (sourceRect.width / 2) - (size / 2);
    const startY = sourceRect.top + (sourceRect.height / 2) - (size / 2);
    const flyingImage = document.createElement('img');
    flyingImage.className = 'cart-fly-image';
    flyingImage.src = sourceImage.currentSrc || sourceImage.src;
    flyingImage.alt = '';
    flyingImage.setAttribute('aria-hidden', 'true');
    flyingImage.style.left = `${startX}px`;
    flyingImage.style.top = `${startY}px`;
    flyingImage.style.width = `${size}px`;
    flyingImage.style.height = `${size}px`;
    flyingImage.style.setProperty('--cart-fly-x', `${endX - (startX + (size / 2))}px`);
    flyingImage.style.setProperty('--cart-fly-y', `${endY - (startY + (size / 2))}px`);
    document.body.appendChild(flyingImage);
    window.requestAnimationFrame(() => flyingImage.classList.add('is-flying'));
    if (cartTarget) {
      cartTarget.classList.remove('is-cart-receiving');
      window.requestAnimationFrame(() => cartTarget.classList.add('is-cart-receiving'));
      window.setTimeout(() => cartTarget.classList.remove('is-cart-receiving'), 1000);
    }
    flyingImage.addEventListener('animationend', () => flyingImage.remove(), { once: true });
    window.setTimeout(() => flyingImage.remove(), 1200);
  };

  const setUpdating = (updating) => {
    isUpdating = updating;
    cartDrawer.toggleAttribute('data-updating', updating);
    cartDrawer.querySelectorAll('button').forEach((button) => {
      if (!button.matches('[data-cart-close]')) button.disabled = updating;
    });
  };

  const updateCartCount = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
      count.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-label', `Shopping bag, ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}`);
    });
    cartDrawer.querySelectorAll('[data-cart-drawer-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
    });
  };

  const cartLine = (item) => {
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-drawer__variant">${escapeHtml(item.variant_title)}</p>`
      : '';
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title || item.title)}" loading="lazy" width="164" height="164">`
      : '';
    const properties = Object.entries(item.properties || {})
      .filter(([name, value]) => value && !name.startsWith('_'))
      .map(([name, value]) => {
        const safeName = escapeHtml(name);
        const safeValue = escapeHtml(value);
        if (typeof value === 'string' && value.includes('/uploads/')) {
          const fileName = escapeHtml(value.split('/').pop() || 'View uploaded file');
          return `<p class="cart-drawer__property">${safeName}: <a href="${safeValue}" target="_blank" rel="noopener">${fileName}</a></p>`;
        }
        return `<p class="cart-drawer__property">${safeName}: ${safeValue}</p>`;
      })
      .join('');
    return `<article class="cart-drawer__line" data-cart-line="${escapeHtml(item.key)}">
      <a class="cart-drawer__image" href="${escapeHtml(item.url)}">${image}</a>
      <div class="cart-drawer__details">
        <div class="cart-drawer__line-top">
          <a class="cart-drawer__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title || item.title)}</a>
          <strong class="cart-drawer__price">${money(item.final_line_price)}</strong>
        </div>
        ${variant}
        ${properties}
        <div class="cart-drawer__controls">
          <div class="cart-drawer__quantity" aria-label="Quantity for ${escapeHtml(item.product_title || item.title)}">
            <button type="button" data-cart-quantity-change="-1" data-cart-line-key="${escapeHtml(item.key)}" aria-label="Decrease quantity">&minus;</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-cart-quantity-change="1" data-cart-line-key="${escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-drawer__remove" type="button" data-cart-remove data-cart-line-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title || item.title)}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5"/></svg>
          </button>
        </div>
      </div>
    </article>`;
  };

  const renderCart = (cart) => {
    updateCartCount(cart);
    if (!cart.item_count) {
      itemsContainer.innerHTML = '<div class="cart-drawer__empty"><p>Your shopping bag is empty.</p><a class="button button--wine" href="/collections/all">Shop products</a></div>';
      footer.hidden = true;
      return;
    }
    itemsContainer.innerHTML = cart.items.map(cartLine).join('');
    total.textContent = money(cart.total_price);
    footer.hidden = false;
  };

  const getCart = async () => {
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  };

  const openCart = async (trigger = document.activeElement) => {
    const sequence = ++cartOpenSequence;
    if (!cartDrawer.classList.contains('is-open')) lastFocusedElement = trigger;
    cartDrawer.inert = false;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    lockCartBackground();
    closeButton.focus();
    setStatus('Loading your shopping bag.');
    try {
      const cart = await getCart();
      if (sequence !== cartOpenSequence) return;
      renderCart(cart);
      setStatus('Shopping bag updated.');
    } catch (error) {
      if (sequence !== cartOpenSequence) return;
      itemsContainer.innerHTML = '<div class="cart-drawer__empty"><p>We could not load your shopping bag. Please try again.</p><a class="button button--wine" href="/cart">View shopping bag</a></div>';
      footer.hidden = true;
      setStatus('Unable to load shopping bag.');
    }
  };

  const closeCart = () => {
    ++cartOpenSequence;
    cartDrawer.classList.remove('is-open');
    document.documentElement.classList.remove('cart-drawer-open');
    unlockCartBackground();
    if (lastFocusedElement?.isConnected) lastFocusedElement.focus();
    cartDrawer.setAttribute('aria-hidden', 'true');
    cartDrawer.inert = true;
  };

  const changeLine = async (key, quantity) => {
    if (isUpdating) return;
    setUpdating(true);
    setStatus('Updating your shopping bag.');
    try {
      const response = await fetch(`${cartUrl}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Unable to update cart.');
      renderCart(await response.json());
      setStatus(quantity === 0 ? 'Item removed from shopping bag.' : 'Shopping bag updated.');
    } catch (error) {
      setStatus('Unable to update shopping bag. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  document.addEventListener('click', (event) => {
    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle) {
      event.preventDefault();
      openCart(cartToggle);
      return;
    }
    if (event.target.closest('[data-cart-close]')) {
      closeCart();
      return;
    }
    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      changeLine(removeButton.dataset.cartLineKey, 0);
      return;
    }
    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    if (!quantityButton) return;
    const line = quantityButton.closest('[data-cart-line]');
    const current = Number.parseInt(line?.querySelector('.cart-drawer__quantity span')?.textContent, 10) || 1;
    const nextQuantity = Math.max(0, current + Number.parseInt(quantityButton.dataset.cartQuantityChange, 10));
    changeLine(quantityButton.dataset.cartLineKey, nextQuantity);
  });

  document.addEventListener('keydown', (event) => {
    if (!cartDrawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); closeCart(); }
    if (event.key === 'Tab') {
      const targets = cartFocusable();
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (!first) { event.preventDefault(); panel.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !targets.includes(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !targets.includes(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.product-form');
    if (!form || event.submitter?.closest('.shopify-payment-button')) return;
    if (form.classList.contains('packaging-page__product-form')) return;
    event.preventDefault();
    const submitButton = form.querySelector('[data-product-submit]');
    if (submitButton?.disabled || isUpdating) return;
    const feedbackButtons = productFeedbackButtons(form);
    const previousDisabledStates = feedbackButtons.map((button) => button.disabled);
    const isMobile = window.matchMedia('(max-width: 800px)').matches;
    setUpdating(true);
    feedbackButtons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    try {
      const response = await fetch(`${cartUrl}/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error('Unable to add item to cart.');
      await response.json();
      animateProductToCart(form);
      showAddedButtonState(feedbackButtons);
      if (isMobile) showCartToast('Added to cart');
      setStatus('Item added to shopping bag.');
      try {
        renderCart(await getCart());
      } catch (cartError) {
        setStatus('Item added. Refresh the page to update the shopping bag count.');
      }
      if (!isMobile) openCart(submitButton);
    } catch (error) {
      setStatus('Unable to add this item. Please try again.');
      if (isMobile) {
        showCartToast("Couldn't add this item. Please try again.");
      } else {
        window.alert('This item could not be added to the shopping bag. Please try again.');
      }
    } finally {
      feedbackButtons.forEach((button, index) => {
        button.disabled = previousDisabledStates[index];
        button.removeAttribute('aria-busy');
      });
      setUpdating(false);
    }
  });
}
